
import { Component, namespace, Provide, Vue } from 'nuxt-property-decorator'
import { Context } from '@nuxt/types'
import type { MetaInfo } from 'vue-meta'
import type { RawLocation, Route } from 'vue-router'
import { HomeOffer, createAmpOfferInstance, OfferDTO } from '@alao-frontend/core'
import { OnBeforeRouteLeave, WithAsyncData, WithHead } from '~/core/vue.types'
import OfferCard from '~/components/offers/OfferCard/OfferCard.vue'
import AddressModalContent from '~/components/filters/AddressModalContent.vue'
import { GettersType as partnerGetterType, namespace as partnerModuleNamespace, Partner } from '~/store/modules/Partner'
import SaveRouteMixin from '~/mixins/save-route.mixin'
import { getExternalCompareMobileHref, getExternalCompareHomeHref } from '~/utils/external-links.utils'
import SkeletonBox from '~/components/comparator/SkeletonBox.vue'
import IconDeliver from '~/assets/icons/icon-deliver.svg?inline'
import IconFace from '~/assets/icons/icon-face.svg?inline'
import IconFind from '~/assets/icons/icon-find.svg?inline'
import IconPayout from '~/assets/icons/icon-payout.svg?inline'
import IconScroll from '~/assets/icons/icon-scroll.svg?inline'
import IconPartnerBG from '~/assets/icons/icon-partner-bg.svg?inline'
import PartnerPromo from '~/components/partners/PartnerPromo.vue'
import { AdditionalQueryParam } from '~/core/osp/additional-query-params'
import { useSEOMetaComposer } from '~/composables/seo.composable'
import { namespace as baseInfoStore, GetterType as BaseInfoGetterType, BaseInfoState } from '~/store/modules/BaseInfo'

interface SavedDataBeforeCheckAddressModal {
  route: Route,
  offer: HomeOffer,
  isAvailable: boolean,
}

const HowItWorksCards = () => ({
  component: import(
    /* webpackChunkName: "sections" */ '~/components/sections/how-it-works/Cards.vue'
  ),
  delay: 200,
  timeout: 3000,
})
const HowItWorksCardsItem = () => ({
  component: import(
    /* webpackChunkName: "sections" */ '~/components/sections/how-it-works/CardsItem.vue'
  ),
  delay: 200,
  timeout: 3000,
})
const OfferModal = () => ({
  component: import(
    /* webpackChunkName: "comparator-components" */ '~/components/filters/OfferModal.vue'
  ),
  delay: 1000,
  timeout: 3000,
})

const BaseInfoStore = namespace(baseInfoStore)

@Component({
  nuxtI18n: {
    paths: {
      en: '/partner/:slug',
      de: '/partner/:slug',
      fr: '/partenaire/:slug',
    },
  },
  components: {
    OfferCard,
    SkeletonBox,
    HowItWorksCards,
    HowItWorksCardsItem,
    OfferModal,
    AddressModalContent,
    IconDeliver,
    IconPayout,
    IconFace,
    IconFind,
    IconScroll,
    IconPartnerBG,
    PartnerPromo,
  },
  mixins: [SaveRouteMixin],
  layout: 'partner',
})
export default class PartnerPage
  extends Vue
  implements WithAsyncData, WithHead, OnBeforeRouteLeave {
  @BaseInfoStore.Getter(BaseInfoGetterType.HOME_PROVIDERS)
  homeProvidersList!: BaseInfoState['providers']

  private partner!: Partner

  private hiwTheme!: Record<string, string>
  private slug!: string
  private isShowAddressPopup = false
  private savedDataBeforeCheckAddressModal: SavedDataBeforeCheckAddressModal | null =
    null

  private isMounted = false

  @Provide('addressForm')
  addressForm: Record<string, string> = {
    zip: '',
    city: '',
    street: '',
    street_number: '',
  }

  head (): MetaInfo {
    return useSEOMetaComposer(
      this,
      {
        title: this.$t('head.meta.partner.title', {
          partner: this.partner.name,
        }).toString(),
        description: this.$t('head.meta.partner.description').toString(),
        route: { name: 'partner-slug', params: { slug: this.$route.params.slug } },
      },
    )
  }

  asyncData (ctx: Context) {
    const slug: string = ctx.params.slug
    const partner =
      ctx.store.getters[
        `${partnerModuleNamespace}/${partnerGetterType.GET_CURRENT_PARTNER}`
      ]

    if (partner) {
      return {
        slug,
        partner,
        hiwTheme: {
          '--hiw-card-active': partner.color_hex_code,
        },
      }
    }

    ctx.error({
      statusCode: 404,
    })
  }

  get additionalQueryParams () {
    return {
      [AdditionalQueryParam.PARTNER]: this.slug,
      [AdditionalQueryParam.STATE]: this.partner.reset_unfinished_orders
        ? 'initial'
        : undefined,
    }
  }

  get mobileOffers () {
    return this.partner?.mobile_offers?.map(el => createAmpOfferInstance(el)) ?? []
  }

  get homeOffers (): HomeOffer[] {
    return this.partner?.home_offers?.map((el: OfferDTO) => createAmpOfferInstance(el)) ?? []
  }

  mounted () {
    this.$analytics.GTM.sendPageViewEvent({
      routeName: this.$route.name as string,
      pageTitle: this.$metaInfo.title || '',
      fullPath: this.$route.fullPath,
      fromPath: this.$nuxt.context.from ? this.$nuxt.context.from.fullPath : '',
      contentGroup: 'Partner Page',
    })
    this.isMounted = true
  }

  beforeRouteLeave (
    to: Route,
    from: Route,
    next: (to?: RawLocation | false | ((vm: Vue) => any) | void) => void,
  ) {
    const offerName = this.localeRoute({ name: 'compare-home-slug' })?.name
    if (to.name !== offerName) {
      return next()
    }
    if (!to.query.partner) {
      if (!this.savedDataBeforeCheckAddressModal?.isAvailable) {
        const offer = this.homeOffers.find(el => el.slug === to.params.slug)

        if (!offer) {
          throw new Error('There is no offer with this ID')
        }

        this.savedDataBeforeCheckAddressModal = {
          route: to,
          offer,
          isAvailable: false,
        }

        this.isShowAddressPopup = true
        return next(false)
      }

      return next(this.routeToLocationWithPartnerSlug(to))
    }

    return next()
  }

  onAddressSubmit (isAvailable: boolean) {
    this.isShowAddressPopup = false

    this.savedDataBeforeCheckAddressModal = Object.assign(
      {},
      this.savedDataBeforeCheckAddressModal,
      {
        isAvailable,
      },
    )

    if (isAvailable) {
      this.$router.push(this.savedDataBeforeCheckAddressModal.route.fullPath)
    } else {
      this.$router.push(getExternalCompareHomeHref(this.$config.BASE_URL, this.localePath))
    }
  }

  routeToLocationWithPartnerSlug (to: Route) {
    return {
      path: to.path,
      name: to.name ?? undefined,
      hash: to.hash,
      query: {
        ...to.query,
        partner: this.partner.slug,
      },
      params: to.params,
    }
  }

  isShouldShow (section: string) {
    return !this.partner.excluded_blocks?.includes(section)
  }

  get externalCompareMobileHref (): string {
    return getExternalCompareMobileHref(this.$config.BASE_URL, this.localePath)
  }

  get externalCompareHomeHref (): string {
    return getExternalCompareHomeHref(this.$config.BASE_URL, this.localePath)
  }
}
